import { createSlice } from '@reduxjs/toolkit';

import {
  getAiAvatarsTaskList,
  createAiAvatarsTaskFromUploaded,
  createAiAvatarsTaskFromGallery,
  goToTheAiAvatarsTask,
  removeAiAvatarsTask,
  sendAiAvatarsImageFeedback,
} from './operations';

import { TESTTASK_ID, TEST_IMAGE_ID } from 'utils/configs';
import { AVATAR_PARAMS_CONFIG } from 'utils/aiAvatarsConfig';

import defaultInputImage from 'media/images/test_ai_model_input.jpg';
import defaultResultImage from 'media/images/test_ai_model_result.jpg';

const defaultTask = {
  id: TESTTASK_ID,
  images: [
    {
      id: TEST_IMAGE_ID,
      input_image: defaultInputImage,
      input_image_small: defaultInputImage,
      input_image_large: defaultInputImage,
      result_image: defaultResultImage,
      result_image_basic: defaultResultImage,
      result_image_large: defaultResultImage,
    },
  ],
  status: 'test',
};

let aiAvatarsInitialState = {
  taskList: [],
  taskDetails: null,
  taskStatus: null,
  avatarParams: Object.keys(AVATAR_PARAMS_CONFIG).reduce((acc, key) => {
    acc[key] = AVATAR_PARAMS_CONFIG[key].defaultValue;
    return acc;
  }, {}),
  isLoading: false,
  responseErrorMessage: null,
};

const handlePending = state => {
  state.responseErrorMessage = null;
  state.isLoading = true;
};

const handleRejected = (state, { payload }) => {
  state.responseErrorMessage = payload;
  state.isLoading = false;
};

export const aiAvatarsSlice = createSlice({
  name: 'aiavatars',
  initialState: aiAvatarsInitialState,

  reducers: {
    resetTaskDetails(state) {
      state.taskDetails = null;
      state.taskStatus = null;
    },

    setAIAvatarParamsValue(state, { payload }) {
      const { field, value } = payload;
      const config = AVATAR_PARAMS_CONFIG[field];
      const currentValue = state.avatarParams[field];
      const newValue = config?.type === 'boolean' ? !currentValue : value;

      if (currentValue !== newValue) {
        state.avatarParams[field] = newValue;
      }
    },

    resetAvatarParams(state) {
      state.avatarParams = aiAvatarsInitialState.avatarParams;
    },

    resetTaskList(state) {
      state.taskList = [];
    },
  },

  extraReducers: builder => {
    builder
      .addCase(getAiAvatarsTaskList.pending, handlePending)
      .addCase(getAiAvatarsTaskList.rejected, handleRejected)
      .addCase(getAiAvatarsTaskList.fulfilled, (state, { payload }) => {
        if (payload.length > 0) {
          state.taskList = payload;
        } else {
          state.taskList = [defaultTask];
        }
        state.isLoading = false;
        state.responseErrorMessage = null;
      })
      .addCase(createAiAvatarsTaskFromUploaded.pending, handlePending)
      .addCase(createAiAvatarsTaskFromUploaded.rejected, handleRejected)
      .addCase(createAiAvatarsTaskFromUploaded.fulfilled, state => {
        state.isLoading = false;
        state.responseErrorMessage = null;
      })
      .addCase(createAiAvatarsTaskFromGallery.pending, handlePending)
      .addCase(createAiAvatarsTaskFromGallery.rejected, handleRejected)
      .addCase(createAiAvatarsTaskFromGallery.fulfilled, state => {
        state.isLoading = false;
        state.responseErrorMessage = null;
      })
      .addCase(goToTheAiAvatarsTask.pending, handlePending)
      .addCase(goToTheAiAvatarsTask.rejected, handleRejected)
      .addCase(goToTheAiAvatarsTask.fulfilled, (state, { payload }) => {
        state.taskDetails = payload;
        state.taskStatus = payload.status;
        state.isLoading = false;
        state.responseErrorMessage = null;
      })
      .addCase(removeAiAvatarsTask.pending, handlePending)
      .addCase(removeAiAvatarsTask.rejected, handleRejected)
      .addCase(removeAiAvatarsTask.fulfilled, (state, { payload }) => {
        state.taskList = state.taskList.filter(task => task.id !== payload);
        state.isLoading = false;
        state.responseErrorMessage = null;
      })
      .addCase(sendAiAvatarsImageFeedback.pending, handlePending)
      .addCase(sendAiAvatarsImageFeedback.rejected, handleRejected)
      .addCase(sendAiAvatarsImageFeedback.fulfilled, (state, { payload }) => {
        const imageArrCopy = [...state.taskDetails.images];
        const updatedImage = state.taskDetails.images.find(
          el => el.id === payload.imageId
        );
        if (updatedImage) updatedImage.feedback = payload.value;

        state.taskDetails.images = imageArrCopy;
        state.isLoading = false;
        state.responseErrorMessage = null;
      });
  },
});

export const {
  setAIAvatarParamsValue,
  resetAvatarParams,
  resetTaskDetails,
  resetTaskList,
} = aiAvatarsSlice.actions;

export const aiAvatarsReducer = aiAvatarsSlice.reducer;
